import { AnimatePresence, motion } from 'motion/react';

import type React from 'react';

type SendButtonState = 'idle' | 'loading' | 'error' | 'success';

export interface SendButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  state: SendButtonState;
}

const iconVariants = {
  idle: { opacity: 1, scale: 1 },
  loading: { opacity: 1, scale: 1 },
  error: { opacity: 1, scale: 1 },
  success: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.8 },
};

export const SendButton: React.FC<SendButtonProps> = ({ state, disabled, ...props }) => {
  return (
    <button
      type='button'
      disabled={disabled || state === 'loading'}
      style={{
        background: disabled ? 'var(--dark-60)' : 'var(--dark-90)',
        border: 'none',
        padding: 2,
        borderRadius: 999,
        cursor: disabled || state === 'loading' ? 'not-allowed' : 'pointer',
        position: 'relative',
        width: 30,
        height: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      aria-label='Send'
      {...props}
    >
      <AnimatePresence mode='wait' initial={false}>
        {state === 'idle' && (
          <motion.svg
            key='arrow'
            width={24}
            height={24}
            viewBox='0 0 24 24'
            fill='none'
            stroke={disabled ? 'var(--dark-40)' : 'var(--neutral-white)'}
            strokeWidth={1}
            strokeLinecap='round'
            strokeLinejoin='round'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <motion.path d='M12 19V5M6 11l6-6 6 6' variants={iconVariants} />
          </motion.svg>
        )}
        {state === 'loading' && (
          <motion.svg
            key='loader'
            width={24}
            height={24}
            viewBox='0 0 24 24'
            fill='none'
            stroke='var(--neutral-white)'
            strokeWidth={1}
            strokeLinecap='round'
            strokeLinejoin='round'
            initial={{ opacity: 0, rotate: -90 }}
            animate={{ opacity: 1, rotate: 0 }}
            exit={{ opacity: 0, rotate: 90 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            style={{ originX: '50%', originY: '50%' }}
          >
            <motion.circle
              cx={12}
              cy={12}
              r={9}
              strokeDasharray='56'
              strokeDashoffset='40'
              animate={{
                rotate: 360,
                transition: {
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear',
                },
              }}
            />
          </motion.svg>
        )}
        {state === 'error' && (
          <motion.svg
            key='error'
            width={24}
            height={24}
            viewBox='0 0 24 24'
            fill='none'
            stroke='var(--neutral-white)'
            strokeWidth={1}
            strokeLinecap='round'
            strokeLinejoin='round'
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <motion.line x1={6} y1={6} x2={18} y2={18} />
            <motion.line x1={6} y1={18} x2={18} y2={6} />
          </motion.svg>
        )}
        {state === 'success' && (
          <motion.svg
            key='success'
            width={24}
            height={24}
            viewBox='0 0 24 24'
            fill='none'
            stroke='var(--neutral-white)'
            strokeWidth={1}
            strokeLinecap='round'
            strokeLinejoin='round'
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <motion.path
              d='M5 13l4 4L19 7'
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.4 }}
            />
          </motion.svg>
        )}
      </AnimatePresence>
    </button>
  );
};
